/**
 * Adapter: webshop listing row (+ its decoration calls) → ProductCardModel.
 *
 * PURE. Fetches nothing, touches no DOM, references no globals beyond Intl.
 * Orchestration (which calls to make, in what order) belongs to the grid;
 * this file only knows how to translate shapes. That split is what lets the
 * whole card be exercised against fixtures with no bench running.
 *
 * Consolidates what are currently seven separate round-trips decorating the
 * same card — see PRODUCT_CARD_PLAN.md §7 "Phase 0 finding".
 */

import { createModel, ACTION_KIND, STOCK_STATE } from '../model.js';

const TITLE_MAX = 90; // parity with webshop grid.js:25

/* ── numbers & money ──────────────────────────────────────────────────────
 * Ported from raftor_webhq/public/js/product_card_contract.js (webhqPriceRibbon).
 * Behaviour preserved exactly; only the output changed — that file built an
 * HTML string, this returns data and lets the renderer decide the markup.
 */

function toNumber(value) {
	if (typeof value === 'number' && isFinite(value)) return value;
	if (!value) return null;
	const n = parseFloat(String(value).replace(/[^0-9.]/g, ''));
	return isFinite(n) && n > 0 ? n : null;
}

function currencySymbol(formatted) {
	if (!formatted) return '';
	const m = String(formatted).match(/^\s*([^\d\s.,-]+)/);
	return m ? m[1] : '';
}

const MONEY = new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 });

export function formatMoney(value) {
	return value == null ? '' : MONEY.format(value);
}

export function adaptPrice(raw) {
	const rate = toNumber(raw.price_list_rate) || toNumber(raw.formatted_price);
	// A price that is really 0 (a price was set and it is 0: the shop formatted it) is not the same as no price (a variant template has none): the card may say Free.
	if (!rate && raw.price_list_rate === 0 && raw.formatted_price) return { current: 0, compare: null, discountPct: null, currency: '', free: true };
	if (!rate) return { current: null, compare: null, discountPct: null, currency: '' };

	const mrp = toNumber(raw.mrp) || toNumber(raw.formatted_mrp);
	const currency =
		currencySymbol(raw.formatted_price) || currencySymbol(raw.formatted_mrp) || '';

	let discountPct = null;
	if (mrp && mrp > rate) {
		discountPct = Math.round((1 - rate / mrp) * 100);
		const serverPct = toNumber(raw.discount_percent);
		if (serverPct && serverPct >= 1) discountPct = Math.round(serverPct);
		if (discountPct < 1) discountPct = null;
	}

	return {
		current: rate,
		compare: mrp && mrp > rate ? mrp : null,
		discountPct,
		currency,
		// A variant template has no price of its own; raftor_webhq fills in the
		// cheapest sellable variant and flags it, so the card can say "From X"
		// rather than implying a single price.
		isFrom: Boolean(raw.price_is_from),
	};
}

/* ── identity ─────────────────────────────────────────────────────────────
 * INVARIANT 1: route-derived, never code-derived. See model.js.
 * Ported from infinite_scroll.js itemKey()/normalizeRoute().
 */

export function normalizeRoute(href) {
	if (!href) return null;
	let route = String(href).replace(/^https?:\/\/[^/]+/, '').split('?')[0].split('#')[0];
	route = route.replace(/^\/+/, '').replace(/\/+$/, '');
	return route ? `/${route}` : null;
}

function adaptIdentity(raw) {
	const route = normalizeRoute(raw.route);
	return {
		id: route || raw.item_code || raw.name || null,
		href: route || '#',
		itemCode: raw.item_code || raw.name || null,
	};
}

/* ── colour swatches ──────────────────────────────────────────────────────
 * Item Attribute has no hex field, so colour is resolved from the value name.
 * Resolution order ported verbatim from product_variant_swatches.js:
 *   CSS named colour → curated apparel map → last word → null.
 */

const SWATCH_COLOR_MAP = {
	'jet black': '#0a0a0a',
	'espresso brown': '#4e342e',
	'chocolate brown': '#5d4037',
	'stone grey': '#8d8d8d',
	'washed grey': '#9e9e9e',
	charcoal: '#36454f',
	khaki: '#b7a878',
	beige: '#d9c7a7',
	'off white': '#f5f2e9',
	cream: '#f3e9d2',
};

// A tiny allowlist beats trusting arbitrary strings as CSS colours; in a
// browser the original used the CSS parser, which is unavailable when this
// module is exercised headlessly under fixtures.
const CSS_NAMED = new Set([
	'black', 'white', 'red', 'green', 'blue', 'yellow', 'orange', 'purple',
	'pink', 'brown', 'grey', 'gray', 'navy', 'teal', 'olive', 'maroon',
	'beige', 'tan', 'gold', 'silver', 'ivory', 'khaki', 'coral', 'salmon',
	'lavender', 'turquoise', 'indigo', 'violet', 'crimson', 'magenta', 'cyan',
]);

function cssColorIfValid(name) {
	return CSS_NAMED.has(name) ? name : null;
}

/** Pure normalization helper -- called once by the orchestrator (grid.js)
 * on the raw {attribute_value: hex} map fetched from
 * raftor_webhq.catalog.api.get_swatch_color_overrides, so every per-card
 * resolveSwatchColor() call below does a cheap lowercase-key lookup instead
 * of re-normalizing the whole map on every card. */
export function normalizeSwatchOverrides(raw) {
	const normalized = {};
	Object.keys(raw || {}).forEach((key) => {
		const value = raw[key];
		if (value) normalized[String(key).trim().toLowerCase()] = value;
	});
	return normalized;
}

export function resolveSwatchColor(name, overrides) {
	const normalized = String(name || '').trim().toLowerCase();
	if (!normalized) return null;
	// Explicit, admin-set color (Item Attribute Value.custom_swatch_color,
	// see catalog/api.py's get_swatch_color_overrides) always wins over any
	// guess below -- it's the escape hatch for a name the heuristic can't
	// parse, or two differently-named values that would otherwise guess to
	// the same color (e.g. "Rose Gold" and "Antique Gold" both end in
	// "Gold" and would collide on the last-word guess).
	return (
		(overrides && overrides[normalized]) ||
		cssColorIfValid(normalized.replace(/[\s_-]+/g, '')) ||
		SWATCH_COLOR_MAP[normalized] ||
		cssColorIfValid(normalized.split(/\s+/).pop()) ||
		null
	);
}

function isColourAttribute(name) {
	return /^colou?r$/i.test(String(name || '').trim());
}

function dedupeAttributes(attributes) {
	// Templates can carry duplicate "Item Variant Attribute" rows from past
	// data imports, which makes the API return the same attribute twice.
	const seen = new Set();
	return (attributes || []).filter((attr) => {
		if (!attr || seen.has(attr.attribute)) return false;
		seen.add(attr.attribute);
		return true;
	});
}

const SAFE_ICON = /^\/(assets|files)\/[A-Za-z0-9_.\/ -]+\.(svg|png|webp)$/;

/** The server only sends safe icon addresses; this is the same rule again where the address is put into a style. */
function safeIcon(address) {
	return typeof address === 'string' && SAFE_ICON.test(address) && address.indexOf('..') === -1 ? address : null;
}

function adaptVariantAttributes(entry, swatchColorOverrides, attributeIcons) {
	const attributes = dedupeAttributes(entry && entry.attributes);
	const swatches = [];
	const sizes = [];

	attributes.forEach((attr) => {
		const values = attr.values || [];
		const icons = attributeIcons && attributeIcons[attr.attribute];
		if (icons) {
			// An attribute the site shows as Icons is drawn like Colour: swatches on the left (icons instead of colours), the size dropdown on the right.
			values.forEach((value) =>
				swatches.push({ label: value, value, icon: safeIcon(icons[value]), color: '#c4c4c4', available: true, attribute: attr.attribute })
			);
		} else if (isColourAttribute(attr.attribute)) {
			values.forEach((value) =>
				swatches.push({
					label: value,
					value,
					color: resolveSwatchColor(value, swatchColorOverrides) || '#c4c4c4',
					available: true,
					attribute: attr.attribute,
				})
			);
		} else {
			values.forEach((value) =>
				sizes.push({ label: value, value, available: true, attribute: attr.attribute })
			);
		}
	});

	return { swatches, sizes };
}

/* ── stock ────────────────────────────────────────────────────────────────
 * Parity with webshop grid.js get_stock_availability(): the stock line only
 * renders at all when settings.show_stock_availability is on, and never for
 * a template with variants.
 */

function adaptStock(raw, settings, badges) {
	const show = Boolean(settings.show_stock_availability) && !raw.has_variants;
	const qty = raw.stock_qty != null ? raw.stock_qty : null;

	if (!show) return { state: STOCK_STATE.IN, label: '', qty };

	if (raw.on_backorder) {
		return { state: STOCK_STATE.BACKORDER, label: 'Available on backorder', qty };
	}
	// dual_stock: {rs_qty, hub_qty, hub_label} — only present when Hub's
	// own Supply Chain HQ Settings.reveal_hub_stock switch is on and this
	// is a Hub-origin item (see raftor_supplychainhq.dual_stock's bulk
	// resolver, wired into get_products below). hub_label is the real,
	// human-set business name for the source (Connection.counterparty_label,
	// resolved server-side) -- a dealer has no reason to know the internal
	// role name "Hub", so this never hardcodes it.
	if (!raw.in_stock) {
		// Local (rs_qty) stock is zero, but dual_stock may still show real
		// combined stock at the Hub -- exactly the situation dual_stock
		// exists to communicate, and previously this branch returned "Out
		// of stock" unconditionally without ever checking dual_stock. Found
		// live 2026-09-18: an Unlimited item with RS-local stock run down
		// to 0 showed no Hub figure on its own card at all.
		if (raw.dual_stock && raw.dual_stock.rs_qty + raw.dual_stock.hub_qty > 0) {
			return {
				state: STOCK_STATE.IN,
				label: `In stock (${raw.dual_stock.rs_qty} · ${raw.dual_stock.hub_label}: ${raw.dual_stock.hub_qty})`,
				qty,
			};
		}
		return { state: STOCK_STATE.OUT, label: 'Out of stock', qty };
	}
	// "Low Stock" is already computed server-side as an auto Item Badge
	// (recompute_item_badges, threshold qty <= 5) — reuse it rather than
	// re-deriving a threshold on the client where it would drift. Kept
	// ahead of dual_stock below, unchanged from before this fix: when
	// local stock is positive but low, the urgency badge still wins over
	// the more informational combined-stock breakdown.
	if ((badges || []).some((b) => b.label === 'Low Stock')) {
		return { state: STOCK_STATE.LOW, label: 'Only a few left', qty };
	}
	if (raw.dual_stock) {
		return {
			state: STOCK_STATE.IN,
			label: `In stock (${raw.dual_stock.rs_qty} · ${raw.dual_stock.hub_label}: ${raw.dual_stock.hub_qty})`,
			qty,
		};
	}
	return { state: STOCK_STATE.IN, label: '', qty };
}

/* ── action kind ──────────────────────────────────────────────────────────
 * Parity with webshop grid.js get_primary_button(). The kinds core has no
 * concept of (whatsapp/chat/readmore) need data that does not exist yet and
 * are deliberately not derivable here — see PRODUCT_CARD_PLAN.md §5.
 */

function adaptActionKind(raw, settings, identity) {
	if (raw.has_variants) return { kind: ACTION_KIND.EXPLORE };
	// An app's own action for the item (a report that is given, a pooja that asks for details first) stands where Add to Cart would: the card leads to it.
	if (raw.app_action && raw.app_action.href && raw.app_action.label) return { kind: ACTION_KIND.EXPLORE, exploreLabel: raw.app_action.label, exploreHref: raw.app_action.href };

	// disable_backorder ("Limited") narrows the store-wide allow_items_not_in_stock
	// allowance, it never grants purchasability on its own -- mirrors
	// stock_guard.get_stock_guard_data's own can_backorder rule server-side.
	// Previously this ignored raw.disable_backorder entirely, so any store with
	// allow_items_not_in_stock on (a normal setup -- it's what lets ordinary
	// items backorder) showed a live Add to Cart button on a Limited item's card
	// even once its real stock hit 0, since settings.allow_items_not_in_stock
	// alone made purchasable true regardless of raw.in_stock.
	const canBackorder = Boolean(settings.allow_items_not_in_stock) && !raw.disable_backorder;
	const purchasable = Boolean(settings.enabled) && (raw.in_stock || canBackorder);

	// INVARIANT 2: never emit 'cart' without something to add.
	if (purchasable && identity.itemCode) {
		return {
			kind: ACTION_KIND.CART,
			addLabel: settings.enable_checkout ? 'Add to Cart' : 'Add to Quote',
			gotoLabel: settings.enable_checkout ? 'Go to Cart' : 'Go to Quote',
			gotoHref: '/cart',
		};
	}
	return { kind: ACTION_KIND.SOLDOUT };
}

/* ── title ────────────────────────────────────────────────────────────────── */

function adaptTitle(raw) {
	const title = raw.web_item_name || raw.item_name || raw.item_code || '';
	return title.length > TITLE_MAX ? `${title.substr(0, TITLE_MAX)}...` : title;
}

/* ── countdown ─────────────────────────────────────────────────────────────
 * A time-limited badge is the sale. get_item_badges already returns
 * `valid_upto` per row and has already dropped inactive ones, so the soonest
 * end date across a product's badges is its countdown — no new data needed.
 */

function adaptCountdown(badges) {
	let earliest = null;
	for (const badge of badges || []) {
		if (!badge.endsAt) continue;
		const ts = Date.parse(badge.endsAt);
		if (!isFinite(ts)) continue;
		if (earliest === null || ts < earliest) earliest = ts;
	}
	return { endsAt: earliest === null ? null : new Date(earliest).toISOString() };
}

function initialsOf(title) {
	// Stand-in for frappe.get_abbr(), which is unavailable under fixtures.
	return String(title || '')
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((word) => word[0].toUpperCase())
		.join('');
}

/* ── entry point ──────────────────────────────────────────────────────────── */

/**
 * @param {object} raw          one row from get_products / get_product_filter_data
 * @param {object} settings     the listing response's `settings`
 * @param {object} decorations  merged results of the batched decoration calls:
 *        { badges: [{label, tone}], variantAttributes: {attributes: []},
 *          offers: [], consumerDiscount: number|null, brand: string|null,
 *          rating: {value, count}|null, cartQty: number }
 * @returns {object} ProductCardModel
 */
export function adaptItem(raw, settings, decorations) {
	const src = raw || {};
	const cfg = settings || {};
	const dec = decorations || {};

	const identity = adaptIdentity(src);
	const title = adaptTitle(src);
	const badges = dec.badges || [];
	const { swatches, sizes } = adaptVariantAttributes(dec.variantAttributes, cfg.swatchColorOverrides, cfg.attributeIcons);
	const cartQty = Number(dec.cartQty) > 0 ? Number(dec.cartQty) : 0;

	return createModel({
		id: identity.id,
		href: identity.href,
		itemCode: identity.itemCode,

		media: {
			src: src.website_image || src.thumbnail || null,
			// website_image_alt is the editor's own alt text; the product name
			// is only a fallback for when they have not written one.
			alt: src.website_image_alt || title,
			fallbackInitials: initialsOf(title),
			// [main, second angle, ...]. createModel seeds this from `src` when
			// absent, so a product with no slideshow still behaves.
			gallery: dec.gallery || null,
		},

		badges,
		// brand is selected inline by StableSortProductQuery now; dec.brand
		// remains supported so a source that still supplies it keeps working.
		eyebrow: src.brand || dec.brand || '',
		title,
		category: src.item_group || '',

		price: adaptPrice(src),
		stock: adaptStock(src, cfg, badges),
		countdown: adaptCountdown(badges),

		swatches,
		sizes,
		// sizes[0].attribute is already "the first non-colour attribute" by
		// construction of adaptVariantAttributes' isColourAttribute filter above
		// - reused rather than a second heuristic. This is the only place
		// "which attribute becomes the size-dropdown" is decided.
		sizeDropdown: { attribute: sizes.length ? sizes[0].attribute : null },
		offers: dec.offers || [],
		consumerDiscount: dec.consumerDiscount != null ? dec.consumerDiscount : null,
		rating: dec.rating || null,

		actions: adaptActionKind(src, cfg, identity),

		state: {
			wished: Boolean(src.wished),
			// cart quantity is server-authoritative and page-global; the grid's
			// cart store owns it, this only reflects what it was told.
			inCart: cartQty > 0 || Boolean(src.in_cart),
			cartQty: cartQty || (src.in_cart ? 1 : 0),
		},
	});
}

/** Map a whole listing page. `decorationsById` is keyed by route (INVARIANT 1). */
export function adaptItems(items, settings, decorationsById) {
	const byId = decorationsById || {};
	return (items || []).map((raw) => {
		const route = normalizeRoute(raw.route);
		return adaptItem(raw, settings, byId[route] || {});
	});
}
